import React from 'react';
import { Card, Col, Row, Typography, Tabs, Tag, Space } from 'antd';
import { GlobalOutlined, ShopOutlined, ShoppingOutlined, CompassOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Seo from '../components/seo/Seo';

const { Title, Paragraph, Text } = Typography;

const Section: React.FC<{ data: any[] }>= ({ data }) => (
  <Row gutter={[16, 16]}>
    {data.map((item) => (
      <Col key={item.title} xs={24} md={12} xl={8}>
        <Card title={<Space>{item.icon}<span>{item.title}</span></Space>}>
          <Paragraph style={{ minHeight: 48 }}>{item.desc}</Paragraph>
          <Space wrap>
            {item.tags?.map((t: string) => (
              <Tag key={t} color="blue">{t}</Tag>
            ))}
          </Space>
          <ul style={{ marginTop: 12 }}>
            {item.links.map((l: any) => (
              <li key={l.name}>
                <a href={l.url} target="_blank" rel="noreferrer">{l.name}</a>
              </li>
            ))}
          </ul>
        </Card>
      </Col>
    ))}
  </Row>
);

const PurchaseChannelsPage: React.FC = () => {
  const { t } = useTranslation();

  const overseas = [
    {
      title: t('purchase_channels.items.overseas.dtc.title'),
      icon: <ShopOutlined />,
      desc: t('purchase_channels.items.overseas.dtc.desc'),
      links: [
        { name: 'Louis Vuitton', url: 'https://www.louisvuitton.com' },
        { name: 'Gucci', url: 'https://www.gucci.com' },
        { name: 'Dior', url: 'https://www.dior.com' },
      ],
      tags: [t('purchase_channels.tags.official'), t('purchase_channels.tags.authentic')]
    },
    {
      title: t('purchase_channels.items.overseas.multibrand.title'),
      icon: <ShoppingOutlined />,
      desc: t('purchase_channels.items.overseas.multibrand.desc'),
      links: [
        { name: 'Farfetch', url: 'https://www.farfetch.com' },
        { name: 'SSENSE', url: 'https://www.ssense.com' },
        { name: 'Mytheresa', url: 'https://www.mytheresa.com' },
      ],
      tags: [t('purchase_channels.tags.selection'), t('purchase_channels.tags.global_shipping')]
    },
    {
      title: t('purchase_channels.items.overseas.department.title'),
      icon: <GlobalOutlined />,
      desc: t('purchase_channels.items.overseas.department.desc'),
      links: [
        { name: 'Harrods', url: 'https://www.harrods.com' },
        { name: 'Selfridges', url: 'https://www.selfridges.com' },
        { name: 'Saks', url: 'https://www.saksfifthavenue.com' },
      ],
      tags: [t('purchase_channels.tags.offline_experience')]
    },
    {
      title: t('purchase_channels.items.overseas.watches.title'),
      icon: <CompassOutlined />,
      desc: t('purchase_channels.items.overseas.watches.desc'),
      links: [
        { name: 'Watches of Switzerland', url: 'https://www.watches-of-switzerland.co.uk' },
        { name: 'Bucherer', url: 'https://www.bucherer.com' },
        { name: 'Chrono24', url: 'https://www.chrono24.com' },
      ],
      tags: [t('purchase_channels.tags.watches'), t('purchase_channels.tags.jewelry')]
    },
    {
      title: t('purchase_channels.items.overseas.resale.title'),
      icon: <ShoppingOutlined />,
      desc: t('purchase_channels.items.overseas.resale.desc'),
      links: [
        { name: 'The RealReal', url: 'https://www.therealreal.com' },
        { name: 'Vestiaire Collective', url: 'https://www.vestiairecollective.com' },
        { name: 'Fashionphile', url: 'https://www.fashionphile.com' },
      ],
      tags: [t('purchase_channels.tags.secondhand')]
    }
  ];

  const china = [
    {
      title: t('purchase_channels.items.china.official.title'),
      icon: <ShopOutlined />,
      desc: t('purchase_channels.items.china.official.desc'),
      links: [
        { name: 'Hermès', url: 'https://www.hermes.com' },
        { name: 'Chanel', url: 'https://www.chanel.com' },
        { name: 'Rolex', url: 'https://www.rolex.com' },
      ],
      tags: [t('purchase_channels.tags.official'), t('purchase_channels.tags.appointment')]
    },
    {
      title: t('purchase_channels.items.china.platform.title'),
      icon: <ShoppingOutlined />,
      desc: t('purchase_channels.items.china.platform.desc'),
      links: [
        { name: '天猫奢品', url: 'https://luxury.tmall.com' },
        { name: '京东奢品', url: 'https://www.jd.com' },
        { name: 'Farfetch CN', url: 'https://www.farfetch.cn' },
      ],
      tags: [t('purchase_channels.tags.authentic')]
    },
    {
      title: t('purchase_channels.items.china.watches.title'),
      icon: <CompassOutlined />,
      desc: t('purchase_channels.items.china.watches.desc'),
      links: [
        { name: '中免集团 CDF', url: 'https://www.cdfg.com.cn' },
        { name: 'Chrono24', url: 'https://www.chrono24.com' },
      ],
      tags: [t('purchase_channels.tags.watches'), t('purchase_channels.tags.duty_free')]
    },
    {
      title: t('purchase_channels.items.china.resale.title'),
      icon: <ShoppingOutlined />,
      desc: t('purchase_channels.items.china.resale.desc'),
      links: [
        { name: '红布林', url: 'https://www.plum.cn' },
        { name: '只二', url: 'https://www.zhi-er.com' },
      ],
      tags: [t('purchase_channels.tags.secondhand')]
    }
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Seo
        title={t('purchase_channels.title')}
        description={t('purchase_channels.description')}
        path={'/purchase-channels'}
        alternates={[
          { hrefLang: 'en', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/purchase-channels?lng=en` },
          { hrefLang: 'zh-CN', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/purchase-channels?lng=zh-CN` },
        ]}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: t('purchase_channels.title'),
            description: t('purchase_channels.description'),
            url: `${(import.meta as any).env.VITE_SITE_URL || ''}/purchase-channels`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'Overseas Channels',
            itemListElement: overseas.map((sec, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: sec.title,
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: 'China Channels',
            itemListElement: china.map((sec, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: sec.title,
            })),
          },
        ]}
      />
      <Title level={2}>{t('purchase_channels.title')}</Title>
      <Paragraph>{t('purchase_channels.description')}</Paragraph>
      <Tabs
        defaultActiveKey="overseas"
        items={[
          {
            key: 'overseas',
            label: t('purchase_channels.tab_overseas'),
            children: <Section data={overseas} />
          },
          {
            key: 'china',
            label: t('purchase_channels.tab_china'),
            children: <Section data={china} />
          }
        ]}
      />
      <Paragraph style={{ marginTop: 24 }}>
        <Text type="secondary">{t('purchase_channels.disclaimer')}</Text>
      </Paragraph>
    </div>
  );
};

export default PurchaseChannelsPage;
